import api from './api';

const ticketService = {
  async getTickets(params = {}) {
    try {
      const queueIds = Array.isArray(params.queueIds) ? params.queueIds : [];

      const qs = new URLSearchParams();
      if (params.userId) qs.set('userId', String(params.userId));
      if (queueIds.length) qs.set('queueIds', JSON.stringify(queueIds));
      if (params.status) qs.set('status', params.status);
      if (params.queueId && params.queueId !== 'all') qs.set('queueId', String(params.queueId));
      if (params.searchParam) qs.set('searchParam', String(params.searchParam));
      if (params.showAll) qs.set('showAll', 'true');

      const endpoint = `/api/tickets${qs.toString() ? `?${qs.toString()}` : ''}`;

      const response = await api.get(endpoint);

      if (response && response.success && Array.isArray(response.tickets)) {
        const result = {
          tickets: response.tickets,
          count: response.count || 0,
          hasMore: response.hasMore || false
        };
        return result;
      }

      let tickets = [];
      if (Array.isArray(response)) {
        tickets = response;
      } else if (response?.data && Array.isArray(response.data)) {
        tickets = response.data;
      } else if (response?.tickets && Array.isArray(response.tickets)) {
        tickets = response.tickets;
      }

      const fallbackResult = {
        tickets: tickets,
        count: tickets.length,
        hasMore: false
      };
      return fallbackResult;
    } catch (error) {
      throw error;
    }
  },

  async getActiveTickets(params = {}) {
    try {
      // Obter userId padrão do localStorage se não vier nos params
      const storedUser = JSON.parse(localStorage.getItem('vortiqaUser') || '{}');
      const defaultUserId = storedUser?.user?.id;

      const userId = params.userId ?? defaultUserId;
      const queueIds = Array.isArray(params.queueIds) ? params.queueIds : [];

      const qs = new URLSearchParams();
      qs.set('status', 'open');
      if (userId) qs.set('userId', String(userId));
      if (queueIds.length) qs.set('queueIds', JSON.stringify(queueIds));

      const endpoint = `/api/tickets${qs.toString() ? `?${qs.toString()}` : ''}`;
      const response = await api.get(endpoint);

      if (response && response.success) {
        return response.tickets || [];
      }

      return Array.isArray(response) ? response : (response?.data || []);
    } catch (error) {
      return [];
    }
  },

  async getQueues() {
    try {
      const response = await api.get('/api/queues');
      
      if (response && response.success) {
        return response.data || [];
      }
      
      return Array.isArray(response) ? response : (response?.data || []);
    } catch (error) {
      return [];
    }
  },

  async createTicket(payload) {
    const response = await api.post('/api/tickets', payload);
    return response;
  },

  async acceptTicket(ticketId) {
    try {
      const response = await api.post(`/api/tickets/${ticketId}/accept`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  async generateProtocol(ticketId) {
    try {
      const response = await api.post(`/api/tickets/${ticketId}/protocol`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  async getTicketMessages(ticketId) {
    try {
      const response = await api.get(`/api/tickets/${ticketId}/messages`);
      
      // O backend retorna { success: true, messages: [...] }
      const messagesData = response?.messages || response?.data?.messages || response?.data || [];
      
      return Array.isArray(messagesData) ? messagesData : [];
    } catch (error) {
      return [];
    }
  },

  async getTicketMessagesPaged(ticketId, params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', String(params.search));
    if (params.mediaType) qs.set('mediaType', String(params.mediaType));
    if (params.fromMe === true || params.fromMe === false) qs.set('fromMe', String(params.fromMe));
    const endpoint = `/api/tickets/${ticketId}/messages${qs.toString() ? `?${qs.toString()}` : ''}`;
    const response = await api.get(endpoint);
    return response;
  },

  async sendMessage(ticketId, messageData) {
    try {
      const response = await api.post(`/api/tickets/${ticketId}/messages`, messageData);
      return response;
    } catch (error) {
      throw error;
    }
  },

  async sendMediaMessage(ticketId, file, options = {}) {
    try {
      const form = new FormData();
      form.append('file', file);
      const inferredType = file?.type?.startsWith('image/')
        ? 'image'
        : (file?.type?.startsWith('audio/')
          ? 'audio'
          : (file?.type?.startsWith('video/')
            ? 'video'
            : 'document'));
      if (options.caption) form.append('caption', options.caption);
      if (options.ptt === true || options.ptt === false) form.append('ptt', String(options.ptt));
      if (options.type) {
        form.append('type', options.type);
      } else if (inferredType) {
        form.append('type', inferredType);
      } // 'image' | 'audio' | 'video' | 'document' | 'sticker'

      const response = await api.upload(`/api/tickets/${ticketId}/messages/media`, form);
      return response;
    } catch (error) {
      throw error;
    }
  },

  async resendMessage(ticketId, messageId) {
    return api.post(`/api/tickets/${ticketId}/messages/${messageId}/resend`);
  },

  async transferTicket(ticketId, toUserId) {
    return api.post(`/api/tickets/${ticketId}/transfer`, { toUserId });
  },

  async listWatchers(ticketId) {
    return api.get(`/api/tickets/${ticketId}/watchers`);
  },

  async addWatcher(ticketId, userId) {
    return api.post(`/api/tickets/${ticketId}/watchers`, { userId });
  },

  async removeWatcher(ticketId, userId) {
    return api.request(`/api/tickets/${ticketId}/watchers/${userId}`, { method: 'DELETE' });
  },

  async getWorkspace(ticketId) {
    return api.get(`/api/tickets/${ticketId}/workspace`);
  },

  async getTimeline(ticketId) {
    return api.get(`/api/tickets/${ticketId}/timeline`);
  },

  async getNotes(ticketId) {
    return api.get(`/api/tickets/${ticketId}/notes`);
  },

  async createNote(ticketId, body) {
    return api.post(`/api/tickets/${ticketId}/notes`, { body });
  },

  async markRead(ticketId) {
    return api.request(`/api/tickets/${ticketId}/read`, { method: 'PATCH', body: {} });
  },

  async sendTicketMessage(ticketId, messageData) {
    // Alias para sendMessage para compatibilidade
    return this.sendMessage(ticketId, messageData);
  },

  async updateTicketStatus(ticketId, status, userId = null, queueId = null, extras = {}) {
    try {
      const data = { status };
      if (userId) data.userId = userId;
      if (queueId) data.queueId = queueId;
      if (extras?.note) data.note = extras.note;
      if (extras?.pendingReasonId) data.pendingReasonId = extras.pendingReasonId;
      if (extras?.closureReasonId) data.closureReasonId = extras.closureReasonId;
      
      const response = await api.request(`/api/tickets/${ticketId}`, { method: 'PATCH', body: data });
      return response;
    } catch (error) {
      throw error;
    }
  }
};

export default ticketService;
